import Vue from 'vue';
import createAuth0Client from '@auth0/auth0-spa-js';

/** Define a default action to perform after authentication */
const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

let instance;
const LOCAL_HOSTS = new Set([
  'localhost',
  '127.0.0.1',
  '::1',
  '[::1]',
  '0.0.0.0',
  'host.docker.internal',
]);

function isLocalAuthHost(hostname) {
  return LOCAL_HOSTS.has(hostname) || hostname.endsWith('.localhost') || /^127\.\d+\.\d+\.\d+$/.test(hostname);
}


/** Returns the current instance of the SDK */
export const getInstance = () => instance;

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
export const useAuth0 = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  redirectUri = window.location.origin,
  ...options
}) => {
  if (instance) return instance;

  // LOCAL/TEST ONLY: bypass Auth0 Universal Login using a dummy token that the
  // E2E harness places in localStorage. Restricted to local hostnames so a
  // non-production build on a remote target still uses real auth.
  if (typeof window !== 'undefined'
      && typeof localStorage !== 'undefined'
      && isLocalAuthHost(window.location.hostname)
      && localStorage.getItem('e2e-fake-token')) {
    const fakeToken = localStorage.getItem('e2e-fake-token');
    // Authenticate Apollo synchronously so the very first route guard (which
    // queries roles immediately) already has the bearer (vue-apollo AUTH_TOKEN).
    localStorage.setItem('apollo-token', fakeToken);
    let claims = {};
    try {
      claims = JSON.parse(decodeURIComponent(escape(atob(fakeToken.split('.')[1]))));
    } catch (e) {
      claims = {};
    }
    instance = new Vue({
      data() {
        return {
          isE2EFake: true,
          loading: false,
          isAuthenticated: true,
          user: {
            sub: claims.sub,
            email: claims.email,
            email_verified: true,
            given_name: claims.given_name || 'E2E',
            family_name: claims.family_name || 'Tester',
            name: claims.name || claims.email,
          },
          popupOpen: false,
          error: null,
        };
      },
      methods: {
        loginWithRedirect() {},
        loginWithPopup() {},
        handleRedirectCallback() {},
        getIdTokenClaims() { return Promise.resolve({ ...claims, email_verified: true }); },
        getTokenSilently() { return Promise.resolve(fakeToken); },
        getTokenWithPopup() { return Promise.resolve(fakeToken); },
        logout() {
          localStorage.removeItem('e2e-fake-token');
          localStorage.removeItem('apollo-token');
        },
        refetchUser() {},
      },
    });
    return instance;
  }

  // The 'instance' is simply a Vue object
  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: null,
        popupOpen: false,
        error: null
      };
    },
    /** Use this lifecycle method to instantiate the SDK client */
    async created() {
      // Create a new instance of the SDK client using members of the given options object
      this.auth0Client = await createAuth0Client({
        ...options,
        client_id: options.clientId,
        redirect_uri: redirectUri
      });

      try {
        // If the user is returning to the app after authentication..
        if (
          window.location.search.includes('code=') &&
          window.location.search.includes('state=')
        ) {
          // handle the redirect and retrieve tokens
          const { appState } = await this.auth0Client.handleRedirectCallback();

          this.error = null;

          // Notify subscribers that the redirect callback has happened, passing the appState
          // (useful for retrieving any pre-authentication state)
          onRedirectCallback(appState);
        }
      } catch (e) {
        this.error = e;
      } finally {
        // Initialize our internal authentication state
        this.isAuthenticated = await this.auth0Client.isAuthenticated();
        this.user = await this.auth0Client.getUser();
        this.loading = false;
      }
    },
    methods: {
      /** Authenticates the user using a popup window */
      async loginWithPopup(options, config) {
        this.popupOpen = true;

        try {
          await this.auth0Client.loginWithPopup(options, config);
          this.user = await this.auth0Client.getUser();
          this.isAuthenticated = await this.auth0Client.isAuthenticated();
          this.error = null;
        } catch (e) {
          this.error = e;
          // eslint-disable-next-line
          console.error(e);
        } finally {
          this.popupOpen = false;
        }

        this.user = await this.auth0Client.getUser();
        this.isAuthenticated = true;
      },
      /** Handles the callback when logging in using a redirect */
      async handleRedirectCallback() {
        this.loading = true;
        try {
          await this.auth0Client.handleRedirectCallback();
          this.user = await this.auth0Client.getUser();
          this.isAuthenticated = true;
          this.error = null;
        } catch (e) {
          this.error = e;
        } finally {
          this.loading = false;
        }
      },
      /** Authenticates the user using the redirect method */
      loginWithRedirect(o) {
        return this.auth0Client.loginWithRedirect(o);
      },
      /** Returns all the claims present in the ID token */
      getIdTokenClaims(o) {
        return this.auth0Client.getIdTokenClaims(o);
      },
      /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
      getTokenSilently(o) {
        return this.auth0Client.getTokenSilently(o);
      },
      /** Gets the access token using a popup window */

      getTokenWithPopup(o) {
        return this.auth0Client.getTokenWithPopup(o);
      },
      /** Logs the user out and removes their session on the authorization server */
      logout(o) {
        return this.auth0Client.logout(o);
      },

      // CUSTOM METHOD: REFETCH USER DATA
      async refetchUser() {
        this.user = await this.auth0Client.getUser();
      }
    }
  });

  return instance;
};

// Create a simple Vue plugin to expose the wrapper object throughout the application
export const Auth0Plugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useAuth0(options);
  }
};